"use client"

import React, { useState, useEffect } from "react"
import { Menu, X } from "lucide-react"

const navLinks = [
  { href: "/#what-we-do-logo-design", label: "Logo Design" },
  { href: "/#what-we-do-software-development", label: "Software Development" },
  { href: "/#what-we-do-web-design", label: "Website Development" },
]

export function Navigation({ setHeroVisible }) {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  const toggleMenu = () => {
    setIsOpen(prev => !prev)
    if (setHeroVisible) setHeroVisible(isOpen)
  }

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setIsOpen(false)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Playfair+Display:ital,wght@0,700;1,400&display=swap');

        .nav-root {
          font-family: 'Outfit', 'Poppins', sans-serif;
          position: fixed;
          top: 0;
          width: 100%;
          z-index: 50;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition: box-shadow 0.3s ease;
        }

        .nav-root.scrolled {
          box-shadow: 0 1px 0 #efefef, 0 4px 24px rgba(0,0,0,0.06);
        }

        .nav-root.top {
          border-bottom: 1px solid #f0f0f0;
        }

        .nav-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 40px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        @media (max-width: 640px) {
          .nav-inner {
            padding: 0 20px;
          }
        }

        .nav-desktop-links {
          display: none;
          align-items: center;
          gap: 32px;
        }

        @media (min-width: 768px) {
          .nav-desktop-links {
            display: flex;
          }
        }

        .nav-hamburger {
          display: flex;
          width: 38px;
          height: 38px;
          align-items: center;
          justify-content: center;
          border: 1px solid #e8e8e8;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        @media (min-width: 768px) {
          .nav-hamburger {
            display: none;
          }
        }

        .nav-hamburger:hover {
          border-color: #111;
        }

        .nav-link {
          font-size: 13px;
          font-weight: 500;
          color: #555;
          text-decoration: none;
          letter-spacing: 0.01em;
          position: relative;
          padding-bottom: 2px;
          transition: color 0.2s;
          white-space: nowrap;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 1px;
          background: #111;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.25s ease;
        }

        .nav-link:hover {
          color: #111;
        }

        .nav-link:hover::after {
          transform: scaleX(1);
        }

        .nav-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          text-decoration: none;
          color: white;
          background: #111;
          padding: 11px 22px;
          border-radius: 6px;
          transition: opacity 0.2s;
          font-family: 'Outfit', 'Poppins', sans-serif;
          white-space: nowrap;
        }

        .nav-cta:hover {
          opacity: 0.75;
        }

        .nav-divider {
          width: 1px;
          height: 18px;
          background: #e8e8e8;
          flex-shrink: 0;
        }

        .nav-mobile {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border-bottom: 1px solid #efefef;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08);
          animation: mobileSlide 0.2s ease;
        }

        @media (min-width: 768px) {
          .nav-mobile {
            display: none;
          }
        }

        @keyframes mobileSlide {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .nav-mobile-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 16px 20px 20px;
          display: flex;
          flex-direction: column;
        }

        .nav-mobile-link {
          font-size: 14px;
          font-weight: 500;
          color: #444;
          text-decoration: none;
          padding: 14px 0;
          border-bottom: 1px solid #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: color 0.2s;
          font-family: 'Outfit', 'Poppins', sans-serif;
          letter-spacing: 0.01em;
        }

        .nav-mobile-link:hover {
          color: #111;
        }

        .nav-mobile-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 14px;
          padding: 14px;
          background: #111;
          color: white;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          text-decoration: none;
          font-family: 'Outfit', 'Poppins', sans-serif;
          transition: opacity 0.2s;
        }

        .nav-mobile-cta:hover {
          opacity: 0.75;
        }
      `}</style>

      <nav className={`nav-root ${scrolled ? "scrolled" : "top"}`}>
        <div className="nav-inner">

          {/* Logo */}
          <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img
              src="/Primary Logo-01.png"
              alt="KDPS Logo"
              style={{ height: "64px", width: "auto" }}
            />
          </a>

          {/* Desktop links */}
          <div className="nav-desktop-links">
            {navLinks.map(link => (
              <a key={link.href} href={link.href} className="nav-link">
                {link.label}
              </a>
            ))}
            <div className="nav-divider" />
            <a href="#contact" className="nav-cta">
              Contact Us
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            </a>
          </div>

          {/* Hamburger */}
          <button className="nav-hamburger" onClick={toggleMenu} aria-label="Toggle menu">
            {isOpen
              ? <X size={16} strokeWidth={2.5} />
              : <Menu size={16} strokeWidth={2.5} />
            }
          </button>

        </div>

        {/* Mobile drawer */}
        {isOpen && (
          <div className="nav-mobile">
            <div className="nav-mobile-inner">
              {navLinks.map(link => (
                
                  <a key={link.href}
                  href={link.href}
                  className="nav-mobile-link"
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7v10" />
                  </svg>
                </a>
              ))}
              <a href="#contact" className="nav-mobile-cta" onClick={() => setIsOpen(false)}>
                Contact Us
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  )
}

export default Navigation